import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Modal from '../components/Modal';
import NotificationsModal from '../components/NotificationsModal';
import { AuthContext } from '../context/AuthContext';
import { getUsersByCustomer, createUser, updateUserPassword, updateUserRole, deleteUser, getCustomer } from '../utils/api';
import { useToast } from '../context/ToastContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faPlus, faKey, faTrash, faArrowLeft, faArrowUp, faArrowDown, faBell } from '@fortawesome/free-solid-svg-icons';
import { ROLE_SUPERADMIN, ROLE_FACILITY_MANAGER, ROLE_PLANT_CARETAKER, ROLE_NAMES } from '../utils/roles';

const OrgUsersPage = () => {
        const { customerId } = useParams();
        const { user } = useContext(AuthContext);
        const [users, setUsers] = useState([]);
        const [customer, setCustomer] = useState(null);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState(null);
        const [isAddModalOpen, setIsAddModalOpen] = useState(false);
        const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
        const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
        const [selectedUser, setSelectedUser] = useState(null);
        const [newEmail, setNewEmail] = useState('');
        const [newFirstName, setNewFirstName] = useState('');
        const [newLastName, setNewLastName] = useState('');
        const [newPassword, setNewPassword] = useState('');
        const [newRole, setNewRole] = useState(ROLE_PLANT_CARETAKER);
        const [sortField, setSortField] = useState('email');
        const [sortDirection, setSortDirection] = useState('asc');
        const [isNotificationsModalOpen, setIsNotificationsModalOpen] = useState(false);
        const [notificationsUser, setNotificationsUser] = useState(null);
        const { showToast } = useToast();

        useEffect(() => {
                fetchData();
        }, [customerId]);

        const fetchData = async () => {
                try {
                        const [usersResponse, customerResponse] = await Promise.all([
                                getUsersByCustomer(customerId),
                                getCustomer(customerId)
                        ]);

                        if (!usersResponse.ok) {
                                throw new Error('Failed to fetch users');
                        }
                        if (!customerResponse.ok) {
                                throw new Error('Failed to fetch customer');
                        }

                        const usersData = await usersResponse.json();
                        const customerData = await customerResponse.json();

                        setUsers(usersData.data || []);
                        setCustomer(customerData.data);
                } catch (err) {
                        setError(err.message);
                } finally {
                        setLoading(false);
                }
        };

        const handleCreateUser = async () => {
                try {
                        const response = await createUser({
                                email: newEmail,
                                first_name: newFirstName,
                                last_name: newLastName,
                                password: newPassword,
                                customer_id: parseInt(customerId),
                                role_id: newRole
                        });
                        if (!response.ok) {
                                const data = await response.json();
                                throw new Error(data.message || 'Failed to create user');
                        }
                        const data = await response.json();
                        setUsers(prev => [...prev, data.data]);
                        setIsAddModalOpen(false);
                        setNewEmail('');
                        setNewFirstName('');
                        setNewLastName('');
                        setNewPassword('');
                        setNewRole(ROLE_PLANT_CARETAKER);
                        showToast('success', 'User created successfully');
                } catch (err) {
                        showToast('error', err.message);
                }
        };

        const handleUpdatePassword = async () => {
                try {
                        const response = await updateUserPassword(selectedUser.id, {
                                new_password: newPassword
                        });
                        if (!response.ok) {
                                const data = await response.json();
                                throw new Error(data.message || 'Failed to update password');
                        }
                        setIsPasswordModalOpen(false);
                        setSelectedUser(null);
                        setNewPassword('');
                        showToast('success', 'Password updated successfully');
                } catch (err) {
                        showToast('error', err.message);
                }
        };

        const handleDeleteUser = async () => {
                try {
                        const response = await deleteUser(selectedUser.id);
                        if (!response.ok) {
                                const data = await response.json();
                                throw new Error(data.message || 'Failed to delete user');
                        }
                        setUsers(prev => prev.filter(u => u.id !== selectedUser.id));
                        setIsDeleteModalOpen(false);
                        setSelectedUser(null);
                        showToast('success', 'User deleted successfully');
                } catch (err) {
                        showToast('error', err.message);
                }
        };

        const handleRoleChange = async (targetUser, newRoleId) => {
                const roleId = parseInt(newRoleId, 10);
                try {
                        const response = await updateUserRole(targetUser.id, roleId);
                        if (!response.ok) {
                                const data = await response.json();
                                throw new Error(data.message || 'Failed to update role');
                        }
                        setUsers(prev => prev.map(u =>
                                u.id === targetUser.id
                                        ? { ...u, role: { id: roleId, name: ROLE_NAMES[roleId] } }
                                        : u
                        ));
                        showToast('success', 'Role updated successfully');
                } catch (err) {
                        showToast('error', err.message);
                }
        };

        const openNotificationsModal = (targetUser) => {
                setNotificationsUser(targetUser);
                setIsNotificationsModalOpen(true);
        };

        const openPasswordModal = (user) => {
                setSelectedUser(user);
                setNewPassword('');
                setIsPasswordModalOpen(true);
        };

        const openDeleteModal = (user) => {
                setSelectedUser(user);
                setIsDeleteModalOpen(true);
        };

        const handleSort = (field) => {
                if (sortField === field) {
                        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                } else {
                        setSortField(field);
                        setSortDirection('asc');
                }
        };

        const getSortIcon = (field) => {
                if (sortField !== field) return null;
                return sortDirection === 'asc' ? faArrowUp : faArrowDown;
        };

        const sortedUsers = useMemo(() => {
                const sorted = [...users].sort((a, b) => {
                        let aValue, bValue;

                        switch (sortField) {
                                case 'email':
                                        aValue = a.email || '';
                                        bValue = b.email || '';
                                        break;
                                case 'role':
                                        aValue = a.role?.name || '';
                                        bValue = b.role?.name || '';
                                        break;
                                default:
                                        return 0;
                        }

                        if (typeof aValue === 'string') {
                                const comparison = aValue.localeCompare(bValue);
                                return sortDirection === 'asc' ? comparison : -comparison;
                        }

                        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                });

                return sorted;
        }, [users, sortField, sortDirection]);

        if (user?.roleId !== ROLE_SUPERADMIN && user?.roleId !== ROLE_FACILITY_MANAGER) {
                return <Navigate to="/" replace />;
        }

        if (loading) {
                return <div className="page-gradient min-h-screen flex items-center justify-center">Loading...</div>;
        }

        if (error) {
                return <div className="page-gradient min-h-screen flex items-center justify-center text-status-critical">{error}</div>;
        }

        return (
                <div className="page-gradient min-h-screen">
                        <Header />

                        <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
                                <section className="mb-8">
                                        <Link to="/org" className="inline-flex items-center text-sm text-text-weak hover:text-text-strong mb-4">
                                                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                                                Back to Organizations
                                        </Link>
                                        <div className="flex items-center justify-between">
                                                <div>
                                                        <h1 className="text-3xl font-bold text-text-strong mb-2">Users - {customer?.name}</h1>
                                                        <p className="text-text-weak">Manage users for this organization</p>
                                                </div>
                                                <button
                                                        onClick={() => setIsAddModalOpen(true)}
                                                data-testid="user-new-open"
                                                        className="inline-flex items-center px-4 py-2 bg-brand text-white rounded-xl hover:bg-brand/90 transition-colors"
                                                >
                                                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                                        New User
                                                </button>
                                        </div>
                                </section>

                                <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-card overflow-hidden">
                                        <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                        <tr>
                                                                <th
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                                                        onClick={() => handleSort('email')}
                                                                        data-testid="sort-email"
                                                                >
                                                                        <div className="flex items-center space-x-1">
                                                                                <span>Email</span>
                                                                                {sortField === 'email' ? (
                                                                                        <FontAwesomeIcon icon={getSortIcon('email')} className="text-gray-400" />
                                                                                ) : (
                                                                                        <div className="flex flex-col text-gray-400">
                                                                                                <FontAwesomeIcon icon={faArrowUp} className="text-[8px] -mb-0.5" />
                                                                                                <FontAwesomeIcon icon={faArrowDown} className="text-[8px]" />
                                                                                        </div>
                                                                                )}
                                                                        </div>
                                                                </th>
                                                                <th
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                                                        onClick={() => handleSort('role')}
                                                                        data-testid="sort-role"
                                                                >
                                                                        <div className="flex items-center space-x-1">
                                                                                <span>Role</span>
                                                                                {sortField === 'role' ? (
                                                                                        <FontAwesomeIcon icon={getSortIcon('role')} className="text-gray-400" />
                                                                                ) : (
                                                                                        <div className="flex flex-col text-gray-400">
                                                                                                <FontAwesomeIcon icon={faArrowUp} className="text-[8px] -mb-0.5" />
                                                                                                <FontAwesomeIcon icon={faArrowDown} className="text-[8px]" />
                                                                                        </div>
                                                                                )}
                                                                        </div>
                                                                </th>
                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                                                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                        </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                        {sortedUsers.map(u => (
                                                                <tr key={u.id} data-testid="user-row" data-user-email={u.email} data-user-id={u.id}>
                                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                                                <div className="flex items-center">
                                                                                        <div className="w-8 h-8 bg-brand/10 rounded-full flex items-center justify-center mr-3">
                                                                                                <FontAwesomeIcon icon={faUser} className="text-brand text-sm" />
                                                                                        </div>
                                                                                        <span data-testid="user-email" className="text-sm font-medium text-gray-900">{u.email}</span>
                                                                                </div>
                                                                        </td>
                                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                                                {u.email === user?.email ? (
                                                                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                                                                u.role?.id === ROLE_SUPERADMIN ? 'bg-red-100 text-red-800' :
                                                                                                u.role?.id === ROLE_FACILITY_MANAGER ? 'bg-purple-100 text-purple-800' :
                                                                                                'bg-green-100 text-green-800'
                                                                                        }`}>
                                                                                                {u.role?.name || ROLE_NAMES[ROLE_PLANT_CARETAKER]}
                                                                                        </span>
                                                                                ) : (
                                                                                        <select
                                                                                                value={u.role?.id || ROLE_PLANT_CARETAKER}
                                                                                                onChange={(e) => handleRoleChange(u, e.target.value)}
                                                                                                data-testid="user-role-select"
                                                                                                className="text-xs font-semibold rounded-full px-2 py-1 border-0 cursor-pointer"
                                                                                                style={{
                                                                                                        backgroundColor: u.role?.id === ROLE_SUPERADMIN ? '#fee2e2' :
                                                                                                                u.role?.id === ROLE_FACILITY_MANAGER ? '#f3e8ff' : '#dcfce7',
                                                                                                        color: u.role?.id === ROLE_SUPERADMIN ? '#991b1b' :
                                                                                                                u.role?.id === ROLE_FACILITY_MANAGER ? '#6b21a8' : '#166534'
                                                                                                }}
                                                                                        >
                                                                                                <option value={ROLE_PLANT_CARETAKER}>Plant caretaker</option>
                                                                                                <option value={ROLE_FACILITY_MANAGER}>Facility manager</option>
                                                                                                {user?.roleId === ROLE_SUPERADMIN && <option value={ROLE_SUPERADMIN}>superadmin</option>}
                                                                                        </select>
                                                                                )}
                                                                        </td>
                                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                                                <span data-testid="user-active" className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${u.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                                                                                        {u.active ? 'Active' : 'Inactive'}
                                                                                </span>
                                                                        </td>
                                                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                                                <div className="flex items-center justify-end gap-4">
                                                                                        <button
                                                                                                onClick={() => openPasswordModal(u)}
                                                                                                data-testid="user-password-open"
                                                                                                className="text-brand hover:text-brand/80"
                                                                                                title="Change Password"
                                                                                        >
                                                                                                <FontAwesomeIcon icon={faKey} />
                                                                                        </button>
                                                                                        <button
                                                                                                onClick={() => openNotificationsModal(u)}
                                                                                                data-testid="user-notifications-open"
                                                                                                className={`text-brand hover:text-brand/80 ${u.role?.id === ROLE_SUPERADMIN ? 'invisible' : ''}`}
                                                                                                title="Notification Settings"
                                                                                        >
                                                                                                <FontAwesomeIcon icon={faBell} />
                                                                                        </button>
                                                                                        <button
                                                                                                onClick={() => openDeleteModal(u)}
                                                                                                data-testid="user-delete-open"
                                                                                                className={`text-red-600 hover:text-red-900 ${u.email === user?.email ? 'invisible' : ''}`}
                                                                                                title="Delete User"
                                                                                        >
                                                                                                <FontAwesomeIcon icon={faTrash} />
                                                                                        </button>
                                                                                </div>
                                                                        </td>
                                                                </tr>
                                                        ))}
                                                </tbody>
                                        </table>
                                </div>

                                {users.length === 0 && (
                                        <div className="text-center py-12">
                                                <p className="text-lg text-gray-600">No users found.</p>
                                        </div>
                                )}
                        </main>

                        <Footer />

                        {/* Add User Modal */}
                        <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)}>
                                <form onSubmit={(e) => { e.preventDefault(); handleCreateUser(); }}>
                                        <h3 className="text-lg font-bold mb-4">Create New User</h3>
                                        <div className="space-y-4">
                                                <div>
                                                        <label htmlFor="user-email" className="block text-sm font-medium text-gray-700">
                                                                Email
                                                        </label>
                                                        <input
                                                                type="email"
                                                                id="user-email"
                                                                data-testid="new-user-email"
                                                                value={newEmail}
                                                                onChange={(e) => setNewEmail(e.target.value)}
                                                                className="p-2 border rounded-md w-full"
                                                                placeholder="Enter email address"
                                                        />
                                                </div>
                                                <div className="flex space-x-4">
                                                        <div className="flex-1">
                                                                <label htmlFor="user-first-name" className="block text-sm font-medium text-gray-700">
                                                                        First Name
                                                                </label>
                                                                <input
                                                                        type="text"
                                                                        id="user-first-name"
                                                                        data-testid="new-user-first-name"
                                                                        value={newFirstName}
                                                                        onChange={(e) => setNewFirstName(e.target.value)}
                                                                        className="p-2 border rounded-md w-full"
                                                                        placeholder="First name"
                                                                        required
                                                                />
                                                        </div>
                                                        <div className="flex-1">
                                                                <label htmlFor="user-last-name" className="block text-sm font-medium text-gray-700">
                                                                        Last Name
                                                                </label>
                                                                <input
                                                                        type="text"
                                                                        id="user-last-name"
                                                                        data-testid="new-user-last-name"
                                                                        value={newLastName}
                                                                        onChange={(e) => setNewLastName(e.target.value)}
                                                                        className="p-2 border rounded-md w-full"
                                                                        placeholder="Last name"
                                                                        required
                                                                />
                                                        </div>
                                                </div>
                                                <div>
                                                        <label htmlFor="user-password" className="block text-sm font-medium text-gray-700">
                                                                Password
                                                        </label>
                                                        <input
                                                                type="password"
                                                                id="user-password"
                                                                data-testid="new-user-password"
                                                                value={newPassword}
                                                                onChange={(e) => setNewPassword(e.target.value)}
                                                                className="p-2 border rounded-md w-full"
                                                                placeholder="Enter password"
                                                        />
                                                </div>
                                                <div>
                                                        <label htmlFor="user-role" className="block text-sm font-medium text-gray-700">
                                                                Role
                                                        </label>
                                                        <select
                                                                id="user-role"
                                                                data-testid="new-user-role"
                                                                value={newRole}
                                                                onChange={(e) => setNewRole(parseInt(e.target.value, 10))}
                                                                className="p-2 border rounded-md w-full"
                                                        >
                                                                <option value={ROLE_PLANT_CARETAKER}>Plant caretaker</option>
                                                                <option value={ROLE_FACILITY_MANAGER}>Facility manager</option>
                                                                {user?.roleId === ROLE_SUPERADMIN && <option value={ROLE_SUPERADMIN}>superadmin</option>}
                                                        </select>
                                                </div>
                                        </div>
                                        <div className="mt-6 flex justify-end space-x-4">
                                                <button
                                                        type="button"
                                                        onClick={() => setIsAddModalOpen(false)}
                                                        className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:text-sm"
                                                >
                                                        Cancel
                                                </button>
                                                <button
                                                        type="submit"
                                                        data-testid="new-user-submit"
                                                        disabled={!newEmail.trim() || !newFirstName.trim() || !newLastName.trim() || !newPassword.trim()}
                                                        className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-brand text-base font-medium text-white hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:text-sm disabled:bg-gray-300"
                                                >
                                                        Create
                                                </button>
                                        </div>
                                </form>
                        </Modal>

                        {/* Change Password Modal */}
                        <Modal isOpen={isPasswordModalOpen} onClose={() => setIsPasswordModalOpen(false)}>
                                <form onSubmit={(e) => { e.preventDefault(); handleUpdatePassword(); }}>
                                        <h3 className="text-lg font-bold mb-4">Change Password</h3>
                                        <p className="text-sm text-gray-600 mb-4">Change password for {selectedUser?.email}</p>
                                        <div className="space-y-4">
                                                <div>
                                                        <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
                                                                New Password
                                                        </label>
                                                        <input
                                                                type="password"
                                                                id="new-password"
                                                                data-testid="user-new-password"
                                                                value={newPassword}
                                                                onChange={(e) => setNewPassword(e.target.value)}
                                                                className="p-2 border rounded-md w-full"
                                                                placeholder="Enter new password"
                                                        />
                                                </div>
                                        </div>
                                        <div className="mt-6 flex justify-end space-x-4">
                                                <button
                                                        type="button"
                                                        onClick={() => setIsPasswordModalOpen(false)}
                                                        className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:text-sm"
                                                >
                                                        Cancel
                                                </button>
                                                <button
                                                        type="submit"
                                                        data-testid="user-password-submit"
                                                        disabled={!newPassword.trim() || newPassword.length < 8}
                                                        className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-brand text-base font-medium text-white hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:text-sm disabled:bg-gray-300"
                                                >
                                                        Update
                                                </button>
                                        </div>
                                </form>
                        </Modal>

                        {/* Delete User Modal */}
                        <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
                                <h3 className="text-lg font-bold mb-4">Delete User</h3>
                                <p className="text-sm text-gray-600 mb-4">
                                        Are you sure you want to delete <strong>{selectedUser?.email}</strong>? This action cannot be undone.
                                </p>
                                <div className="mt-6 flex justify-end space-x-4">
                                        <button
                                                type="button"
                                                onClick={() => setIsDeleteModalOpen(false)}
                                                className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:text-sm"
                                        >
                                                Cancel
                                        </button>
                                        <button
                                                type="button"
                                                onClick={handleDeleteUser}
                                                data-testid="user-delete-confirm"
                                                className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
                                        >
                                                Delete
                                        </button>
                                </div>
                        </Modal>

                        <NotificationsModal
                                isOpen={isNotificationsModalOpen}
                                onClose={() => setIsNotificationsModalOpen(false)}
                                targetUser={notificationsUser}
                        />
                </div>
        );
};

export default OrgUsersPage;
